import { useState } from "react";
import { api, type ConflictDetail, type ConflictVersion } from "../api.js";

/**
 * Resolving an edit conflict — docs/sync-state-machine.md §7.
 *
 * The spec writes the screen almost literally:
 *
 *     Piyush + Tarun 15–11 — your version, edited just now
 *     Piyush + Tarun 15–12 — Ashith's version, saved 4 minutes ago
 *
 *     Keep mine · Keep theirs · Enter the correct score
 *
 * Attribution and timestamps, never a diff of raw JSON (CONF-02). The server
 * has already turned both sides into that shape, so this renders rather than
 * interprets.
 *
 * E-2 — "keep theirs" is a real choice that writes a real record, not a
 * dismissal. Six months later, "nobody touched it" and "someone looked and
 * decided" have to be tellable apart.
 */
export function ResolveConflict({
  conflict,
  onResolved,
  onSkip,
}: {
  conflict: ConflictDetail;
  onResolved: () => Promise<void>;
  onSkip: () => void;
}): React.JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [correcting, setCorrecting] = useState(false);
  const [scoreA, setScoreA] = useState(String(conflict.mine.sideAScore));
  const [scoreB, setScoreB] = useState(String(conflict.mine.sideBScore));

  async function resolve(
    choice: string,
    corrected?: { sideAScore: number; sideBScore: number },
  ): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.resolveConflict(conflict.id, {
        choice,
        // E-1 / CONF-05 — the version this screen is showing. If the game moved
        // while the member was deciding, the server refuses and they look again
        // rather than overwriting somebody unseen.
        expectedVersion: conflict.serverVersion,
        corrected,
      });
      await onResolved();
    } catch (e) {
      setError(e instanceof Error ? e.message : "That did not work.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card stack resolve">
      <h2>Two different scores</h2>
      <p>
        This game was saved twice with different results. Nothing has been
        overwritten — pick which is right.
      </p>

      <Version version={conflict.mine} />
      <Version version={conflict.theirs} />

      {error ? <p className="error">{error}</p> : null}

      <div className="row">
        <button className="primary" disabled={busy} onClick={() => void resolve("keep_mine")}>
          Keep mine
        </button>
        <button className="primary" disabled={busy} onClick={() => void resolve("keep_theirs")}>
          Keep theirs
        </button>
      </div>

      <button className="chip" disabled={busy} onClick={() => setCorrecting((v) => !v)}>
        Enter the correct score
      </button>

      {correcting ? (
        <div className="stack">
          <div className="row">
            <label>
              One side
              <input inputMode="numeric" value={scoreA} onChange={(e) => setScoreA(e.target.value)} />
            </label>
            <label>
              The other
              <input inputMode="numeric" value={scoreB} onChange={(e) => setScoreB(e.target.value)} />
            </label>
          </div>
          <button
            className="primary"
            disabled={busy}
            onClick={() =>
              void resolve("corrected", {
                sideAScore: Number(scoreA),
                sideBScore: Number(scoreB),
              })
            }
          >
            Save this score
          </button>
        </div>
      ) : null}

      <button className="link" onClick={onSkip} disabled={busy}>
        Decide later
      </button>
    </section>
  );
}

function Version({ version }: { version: ConflictVersion }): React.JSX.Element {
  const saved = version.savedAt ? new Date(version.savedAt) : null;
  return (
    <div className="version">
      <strong>
        {version.deleted ? "Deleted" : `${version.sideAScore}–${version.sideBScore}`}
      </strong>
      <span className="muted small">
        {" "}
        — {version.attribution}
        {saved
          ? `, saved ${saved.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
          : ""}
        {/* E-4 — a deletion racing an edit says so, rather than showing a score
            that no longer exists. */}
        {version.deleted ? " · this game was removed" : ""}
      </span>
    </div>
  );
}
